import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Footer from './Footer';

const blogPosts = [
  {
    id: 1,
    title: "The Only Checklist You'll Ever Need for an Adequate Unified Identity",
    excerpt: "Consolidating and maintaining a unified identity in a fragmented and everchanging revolve is peace of mind — if nothing else.",
    slug: "/unified-identity",
  },
  {
    id: 2,
    title: "Using Web 2.5 Apps to Reroll and Parlay Your Way to Profitability",
    excerpt: "You don't have to be an eastern spy or have an MBA from Wharton to do big business. Here's a list of the best web 2.5 apps that call on the ability to start generating formal benefits immediately.",
    slug: "/morsels",
  }
];

const BlogPage = () => {
  return (
    <div className="blog-page">
      {/* Header */}
      <header className="blog-header">
        <Link to="/" className="back-button" data-testid="blog-back-button">
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="blog-main">
        <h1 className="blog-page-title">
          Less-Than <span className="highlight-orange">Circlejerkular</span> Watercooler Banter
        </h1>

        {/* Writing GIF */}
        <figure className="writing-gif-container">
          <img 
            src="/img/alan-shteynberg-writing.gif" 
            alt="alan-shteynberg-writing"
            title="alan-shteyberg-writing"
            className="writing-gif"
            loading="eager"
            data-testid="blog-writing-gif"
          />
          <figcaption className="gif-attribution">
            Source: <a href="https://www.wattpad.com/804391790-flix-gifs-specifically-selected-for-writers" target="_blank" rel="noopener noreferrer">Flix | GIFS Specifically Selected For Writers</a>
          </figcaption>
        </figure>

        {/* Blog Posts List */}
        <div className="blog-posts">
          {blogPosts.map((post) => (
            <Link to={post.slug} key={post.id} className="blog-post-link" data-testid={`blog-post-${post.id}`}>
              <article className="blog-post">
                <h2 className="post-title">{post.title}</h2>
                <p className="post-excerpt">{post.excerpt}</p>
                <span className="post-read-more">
                  Read <ArrowRight size={16} />
                </span>
              </article>
            </Link>
          ))}
        </div>
      </main>

      <Footer />

      <style jsx>{`
        .blog-page {
          min-height: 100vh;
          background: #151312;
          padding: 24px;
        }

        .blog-header {
          max-width: 800px;
          margin: 0 auto 40px;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: transparent;
          border: 1px solid rgba(182, 180, 189, 0.3);
          color: #998f8f;
          padding: 10px 16px;
          border-radius: 8px;
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .back-button:hover {
          border-color: #f46c38;
          color: #f46c38;
        }

        .blog-main {
          max-width: 800px;
          margin: 0 auto;
        }

        .blog-page-title {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(36px, 6vw, 56px);
          font-weight: 700;
          color: #fff;
          line-height: 1;
          letter-spacing: -0.02em;
          margin: 0 0 32px 0;
        }

        .highlight-orange {
          color: #f46c38;
        }

        .writing-gif-container {
          margin: 0 0 48px 0;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .writing-gif {
          max-width: 100%;
          height: auto;
          border-radius: 12px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
        }

        .gif-attribution {
          width: 100%;
          max-width: 500px;
          margin-top: 12px;
          font-family: 'Poppins', sans-serif;
          font-size: 12px;
          color: #998f8f;
          text-align: left;
        }

        .gif-attribution a {
          color: #998f8f;
          text-decoration: none;
          transition: opacity 0.2s ease;
        }

        .gif-attribution a:hover {
          opacity: 0.8;
          text-decoration: underline;
        }

        .title-faded {
          color: rgba(182, 180, 189, 0.2);
        }

        .blog-posts {
          display: flex;
          flex-direction: column;
          gap: 40px;
        }

        .blog-post-link {
          text-decoration: none;
          color: inherit;
        }

        .blog-post {
          padding: 32px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(182, 180, 189, 0.1);
          border-radius: 16px;
          transition: border-color 0.2s ease;
        }

        .blog-post-link:hover .blog-post {
          border-color: rgba(244, 108, 56, 0.4);
        }

        .post-title {
          font-family: 'Poppins', sans-serif;
          font-size: 28px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 16px 0;
          line-height: 1.3;
        }

        .post-excerpt {
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          color: #998f8f;
          line-height: 1.7;
          margin: 0 0 20px 0;
        }

        .post-read-more {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: #f46c38;
          transition: gap 0.2s ease;
        }

        .blog-post-link:hover .post-read-more {
          gap: 10px;
        }

        @media (max-width: 600px) {
          .blog-page {
            padding: 16px;
          }

          .blog-post {
            padding: 20px;
          }

          .post-title {
            font-size: 22px;
          }

          .post-excerpt {
            font-size: 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default BlogPage;
